import { compareAsc, eachDayOfInterval, format, isValid, parse, parseISO } from 'date-fns';

import { DateFormat, DateTimeFormat, TimeFormat } from './date-format';

export const DATE_PATTERN = /^\d{2}\.\d{2}\.\d{4}$/;
export { DateFormat, DateTimeFormat, TimeFormat } from './date-format';

type DateString = string | null | undefined;

const API_PREFIX = /^(\d{4}-\d{2}-\d{2})/;
const PARSE_REF = new Date(2000, 0, 1);

function parseStrict(value: string, pattern: DateFormat): Date | null {
  const parsed = parse(value, pattern, PARSE_REF);

  if (!isValid(parsed) || format(parsed, pattern) !== value) {
    return null;
  }

  return parsed;
}

function civilFromUnknown(value: DateString): Date | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  const apiPrefix = API_PREFIX.exec(trimmed)?.[1];

  return (
    parseStrict(trimmed, DateFormat.DISPLAY) ??
    parseStrict(trimmed, DateFormat.API) ??
    (apiPrefix ? parseStrict(apiPrefix, DateFormat.API) : null)
  );
}

export function formatDate(date: Date, to: DateFormat): string {
  return isValid(date) ? format(date, to) : '';
}

export function formatDateTime(date: Date, to: DateTimeFormat = DateTimeFormat.DISPLAY): string {
  return isValid(date) ? format(date, to) : '';
}

const RELATIVE_UNITS: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 60],
  ['month', 30 * 24 * 60],
  ['day', 24 * 60],
  ['hour', 60],
  ['minute', 1],
];

/** Relative phrase for an instant (`2 hours ago`), blank when the value is missing. */
export function formatRelativeTime(iso: DateString, language?: string): string {
  if (!iso) {
    return '';
  }

  const minutes = Math.round((Date.parse(iso) - Date.now()) / 60_000);
  const locale = language === 'no' ? 'nb' : language;
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (Number.isNaN(minutes)) {
    return iso;
  }

  for (const [unit, minutesPerUnit] of RELATIVE_UNITS) {
    if (Math.abs(minutes) >= minutesPerUnit) {
      return formatter.format(Math.round(minutes / minutesPerUnit), unit);
    }
  }

  return formatter.format(0, 'minute');
}

export function formatDateTimeToLocal(value: DateString): string {
  if (!value) {
    return '';
  }

  const date = parseISO(value.trim());

  return isValid(date) ? format(date, DateTimeFormat.DISPLAY) : '';
}

export function formatDateForRequest(dateString: DateString): string {
  const date = civilFromUnknown(dateString);

  return date ? format(date, DateFormat.API) : '';
}

export function formatDateToLocal(dateString: DateString): string {
  const date = civilFromUnknown(dateString);

  return date ? format(date, DateFormat.DISPLAY) : '';
}

export function todayDisplay(): string {
  return formatDate(new Date(), DateFormat.DISPLAY);
}

export function displayDateFromUtc(date: Date): string {
  return formatDate(
    new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
    DateFormat.DISPLAY,
  );
}

export function utcDateFromDisplay(dateString: DateString): Date | null {
  const iso = formatDateForRequest(dateString);

  if (!iso) {
    return null;
  }

  const utc = parseISO(`${iso}T00:00:00Z`);

  return isValid(utc) ? utc : null;
}

export function compareDisplayDates(left: string, right: string): number {
  const a = civilFromUnknown(left);
  const b = civilFromUnknown(right);

  if (!a || !b) {
    return formatDateForRequest(left).localeCompare(formatDateForRequest(right));
  }

  return compareAsc(a, b);
}

export function eachDisplayDay(start: string, end: string): readonly string[] {
  const from = civilFromUnknown(start);
  const to = civilFromUnknown(end);

  if (!from || !to) {
    return [];
  }

  const interval = compareAsc(from, to) <= 0 ? { start: from, end: to } : { start: to, end: from };

  return eachDayOfInterval(interval).map((date) => format(date, DateFormat.DISPLAY));
}

export function formatDateFromSlashedToLocal(dateString: DateString): string {
  if (!dateString) {
    return '';
  }

  const parsed = parseStrict(dateString.trim(), DateFormat.SLASHED);

  return parsed ? format(parsed, DateFormat.DISPLAY) : '';
}

const WALL_CLOCK = /^([01]\d|2[0-3]):[0-5]\d/;

/** Local wall time of an instant as `TimeFormat.DISPLAY` (`HH:mm`). */
export function displayTimeFromInstant(value: DateString): string {
  if (!value) {
    return '';
  }

  const date = parseISO(value.trim());

  return isValid(date) ? format(date, TimeFormat.DISPLAY) : '';
}

/** Local civil date of an instant as `DateFormat.DISPLAY`. */
export function displayDateFromInstant(value: DateString): string {
  if (!value) {
    return '';
  }

  const date = parseISO(value.trim());

  return isValid(date) ? format(date, DateFormat.DISPLAY) : '';
}

/**
 * Combines a DISPLAY date and `HH:mm` wall time in the local zone into a
 * UTC RFC 3339 instant (`…Z`). Empty or incomplete input is `''`.
 */
export function utcIsoFromLocalDateTime(date: DateString, time: DateString): string {
  const isoDate = formatDateForRequest(date);
  const clock = WALL_CLOCK.exec((time ?? '').trim())?.[0];

  if (!isoDate || !clock) {
    return '';
  }

  const local = parse(`${isoDate} ${clock}`, `${DateFormat.API} ${TimeFormat.DISPLAY}`, new Date());

  if (!isValid(local)) {
    return '';
  }

  return local.toISOString().replace(/\.\d{3}Z$/, 'Z');
}
